import { API } from '../../api';
import { AutoComplete, Button, Input, Modal, notification } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { handleError } from '../../utils/util';
import React, { useEffect, useState } from 'react';

const { confirm } = Modal;
const { TextArea } = Input;

const CreateValidationRulesModal = props => {
  const {
    isOpen,
    closeModal,
    hl7VersionOptions,
    contextOptions,
    setParentHL7Version,
    setParentResourceType,
    setParentContext,
    setParentRuleVersion,
  } = props;

  const [resourceType, setResourceType] = useState(null);
  const [ruleVersion, setRuleVersion] = useState(null);
  const [hl7Version, setHL7Version] = useState(null);
  const [existingRuleVersions, setExistingRuleVersions] = useState([]);
  const [context, setContext] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [rules, setRules] = useState(null);
  const [rulesForDisplay, setRulesForDisplay] = useState(null);
  const [warning, setWarning] = useState(false);
  const isParseErrorClass = parseError ? 'parse-error-border' : '';

  const resourceTypeOptions = [
    'Patient',
    'Allergy Intolerance',
    'Condition',
    'Encounter',
    'Observation',
    'Diagnostic Report',
    'Practitioner',
    'Practitioner Role',
    'Procedure',
    'Coverage',
  ].map(val => {
    return { value: val };
  });

  const handleClose = () => {
    setResourceType(null);
    setRuleVersion(null);
    setHL7Version(null);
    setContext(null);
    setRules(null);
    setRulesForDisplay(null);
    setParseError(false);
    setWarning(false);
    setExistingRuleVersions([]);
    closeModal();
  };

  const handleSave = () => {
    confirm({
      title: 'Are you sure?',
      okText: 'Yes',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        const requestBody = {
          hl7_version: hl7Version,
          resourcetype: resourceType,
          context,
          rule_version: ruleVersion,
          rules,
        };

        try {
          const res = await API.post('/hl7-validation-rules', requestBody);

          if (res.status === 201) {
            notification.success({
              message: 'Success!',
              description: 'Rules have been successfully saved',
              duration: 3,
              placement: 'topRight',
            });
            setParentHL7Version(hl7Version);
            setParentContext(context);
            setParentResourceType(resourceType);
            setParentRuleVersion(ruleVersion);
            handleClose();
          } else {
            notification.error({
              description: `(${res.status}) - ${res.body}`,
              duration: 2,
            });
          }
        } catch (e) {
          handleError(e);
        }
      },
    });
  };

  const checkWarning = () => {
    if (rulesForDisplay) {
      setWarning(true);
    }
  };

  const onChangeRules = e => {
    let rulesInput;

    try {
      rulesInput = JSON.parse(e.target.value);
      setParseError(false);
      setRules(rulesInput);
      setRulesForDisplay(JSON.stringify(rulesInput, null, 2));
    } catch (error) {
      rulesInput = e.target.value;
      setParseError(true);
      setRules(null);
      setRulesForDisplay(rulesInput);
    }
  };

  const onChangeHL7Version = version => {
    setExistingRuleVersions([]);
    setHL7Version(version);
    checkWarning();
  };

  const onChangeResourceType = type => {
    setExistingRuleVersions([]);
    setResourceType(type);
    checkWarning();
  };

  const onChangeContext = igContext => {
    setExistingRuleVersions([]);
    setContext(igContext);
    checkWarning();
  };

  const onChangeRuleVersion = version => {
    setRuleVersion(version);
  };

  const getExistingRuleVersions = async () => {
    if (hl7Version && resourceType && context) {
      try {
        const { data: ruleVersions } = await API.get(
          `/hl7-validation-rules/rule-versions?resourcetype=${resourceType}&hl7_version=${hl7Version}&context=${context}`,
        );
        setExistingRuleVersions(ruleVersions);

        if (ruleVersions.length <= 0) {
          notification.info({
            description: `No rules found for HL7v2 Version "${hl7Version}", Resource Type "${resourceType}", Implementation Guide "${context}"`,
            duration: 3,
            placement: 'topRight',
          });
        }
      } catch (e) {
        handleError(e);
      }
    }
  };

  const getLatestRules = async () => {
    let latestRules;

    try {
      const latestRulesRes = await API.get(
        `/hl7-validation-rules?resourcetype=${resourceType}&hl7_version=${hl7Version}&context=${context}`,
      );
      latestRules = latestRulesRes.data;
    } catch (e) {
      handleError(e);
    }

    if (latestRules.length > 0) {
      const filteredRules = latestRules.map(elem => {
        return {
          segment: elem.segment,
          field: elem.field,
          sub_field: elem.sub_field,
          field_description: elem.field_description,
          required: elem.required,
          rule_notes: elem.rule_notes,
          fail_conversion: elem.fail_conversion,
        };
      });
      setRules(filteredRules);
      setRulesForDisplay(JSON.stringify(filteredRules, null, 2));
    } else {
      notification.info({
        description: `No rules found for HL7v2 Version "${hl7Version}", Resource Type "${resourceType}", Implementation Guide "${context}"`,
        duration: 3,
        placement: 'topRight',
      });
      setRulesForDisplay(null);
    }

    setWarning(false);
  };

  const handlePopulateRules = () => {
    if (hl7Version && resourceType && context) {
      confirm({
        title: 'Are you sure? Any changes below will be overwritten.',
        okText: 'Ok',
        okType: 'primary',
        cancelText: 'Cancel',
        onOk: () => {
          getLatestRules();
        },
      });
    } else {
      notification.info({
        description: 'HL7v2 Version, Resource Type, and Implementation Guide are required',
        duration: 3,
        placement: 'topRight',
      });
    }
  };

  useEffect(() => {
    if (document.getElementById('hl7-validation-rules-textarea')) {
      document.getElementById('hl7-validation-rules-textarea').value = rulesForDisplay;
      document.getElementById('hl7-validation-rules-textarea').innerText = rulesForDisplay;
    }
  });

  useEffect(() => {
    if (!rulesForDisplay) {
      setWarning(false);
    }
  }, [rulesForDisplay]);

  return (
    <Modal
      title="Create Validation Rules"
      style={{ top: 10 }}
      width="75%"
      height="100%"
      okText="Save"
      onClose={handleClose}
      onOk={handleSave}
      onCancel={handleClose}
      open={isOpen}
      destroyOnClose
      data-cy="create-hl7-validation-modal"
    >
      <div>
        <span>
          <strong>HL7v2 Version</strong>
          <AutoComplete
            style={{ width: '300px', marginLeft: '20px' }}
            value={hl7Version}
            options={hl7VersionOptions.map(val => {
              return { value: val };
            })}
            onSelect={onChangeHL7Version}
            onChange={onChangeHL7Version}
            data-cy="hl7-validation-input-hl7-version"
          />
        </span>
      </div>
      <div style={{ marginTop: '15px' }}>
        <span>
          <strong>Resource Type</strong>
          <AutoComplete
            style={{ width: '300px', marginLeft: '20px' }}
            value={resourceType}
            options={resourceTypeOptions}
            onSelect={onChangeResourceType}
            onChange={onChangeResourceType}
            data-cy="hl7-validation-input-resourcetype"
          />
        </span>
      </div>
      <div style={{ marginTop: '15px' }}>
        <span>
          <strong>Implementation Guide</strong>
          <AutoComplete
            style={{ width: '300px', marginLeft: '20px' }}
            value={context}
            options={contextOptions.map(val => {
              return { value: val };
            })}
            onSelect={onChangeContext}
            onChange={onChangeContext}
            data-cy="hl7-validation-input-context"
          />
        </span>
      </div>
      <div style={{ marginTop: '15px' }}>
        <span>
          <strong>Rule Version</strong>
          <AutoComplete
            style={{ width: '300px', marginLeft: '20px' }}
            value={ruleVersion}
            onChange={onChangeRuleVersion}
            data-cy="hl7-validation-input-rule-version"
          />
        </span>
      </div>
      {hl7Version && resourceType && context && (
        <div style={{ marginTop: '5px' }}>
          <Button onClick={getExistingRuleVersions}>Get existing rule versions</Button>
        </div>
      )}
      {existingRuleVersions.length > 0 && (
        <div>
          <span style={{ marginRight: '15px' }}>Existing versions:</span>
          {existingRuleVersions.map(val => `"${val}"`).join(', ')}
        </div>
      )}
      <div style={{ marginTop: '25px' }}>
        <span>
          <strong>Rules</strong>
          <Button style={{ marginLeft: '20px', marginRight: '20px' }} onClick={handlePopulateRules}>
            Populate with latest rules
          </Button>
          {warning && (
            <span style={{ color: 'red' }}>
              <WarningOutlined style={{ color: 'red', fontSize: '20px' }} />
              Warning! The HL7 version, resource type, or implementation guide was changed after the
              rules below were entered.
            </span>
          )}
          <TextArea
            style={{ marginTop: '10px' }}
            allowClear
            id="hl7-validation-rules-textarea"
            rows={25}
            className={isParseErrorClass}
            data-cy="hl7-validation-rules-textarea"
            bordered
            showCount
            maxLength={500000}
            onBlur={onChangeRules}
          />
        </span>
      </div>
    </Modal>
  );
};

export default CreateValidationRulesModal;
